import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Skeleton } from '../components/Skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * /relo-news/cities - index of 13 Mundalo city guides.
 * Pure SEO: hero photo, name, tagline, key stats. Lang toggle hands off to
 * the same toggle on /relo-news.
 */
const CityGuides = () => {
  const [cities, setCities] = useState(null);
  const [lang, setLang] = useState('en');

  useEffect(() => {
    setCities(null);
    fetch(`${API_URL}/api/cities${lang === 'es' ? '?lang=es' : ''}`)
      .then((r) => (r.ok ? r.json() : { cities: [] }))
      .then((d) => setCities(d.cities || []))
      .catch(() => setCities([]));
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]">
      <SEO
        title="Mexico City Guides for Americans Moving Abroad - Mundalo"
        description="Honest, granular guides to the 13 best Mexican cities for Americans relocating. Neighborhoods, costs, visas, healthcare, expat community - built by Mundalo's editorial team."
        path="/relo-news/cities"
      />
      <Header currentPage="news" />

      <div className="border-b border-[#0A1F2E]/8 bg-[#F4ECD8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <nav aria-label="Breadcrumb" className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-4">
            <Link to="/relo-news" className="hover:text-[#0A1F2E]">Relo News</Link>
            <span className="mx-2 text-[#0A1F2E]/30">→</span>
            <span className="text-[#0A1F2E]">City Guides</span>
          </nav>
          <h1 className="font-display italic text-5xl md:text-6xl text-[#0A1F2E] leading-[1.05] mb-3">
            13 cities Americans actually move to.
          </h1>
          <p className="font-body font-light text-lg md:text-xl text-[#0A1F2E]/75 max-w-2xl">
            Honest, granular guides - neighborhoods, costs, visa specifics, healthcare,
            expat community. Built from on-the-ground reporting by the Mundalo editorial team.
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex items-center justify-end mb-6">
          <div
            className="inline-flex items-center bg-white rounded-full p-1 border border-[#EFE6CF] shadow-sm"
            role="tablist"
            aria-label="Language"
            data-testid="city-guides-lang-toggle"
          >
            {['en', 'es'].map((l) => (
              <button
                key={l}
                type="button"
                role="tab"
                aria-selected={lang === l}
                onClick={() => setLang(l)}
                data-testid={`city-guides-lang-${l}`}
                className={`px-4 py-1.5 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] transition ${
                  lang === l ? 'bg-[#F9F6EF] text-[#0A1F2E] border-2 border-[#2CA8A0] font-semibold' : 'text-[#0A1F2E]/60'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {!cities ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map((i) => (
              <div key={i}>
                <Skeleton className="aspect-[4/3] mb-3" />
                <Skeleton className="h-5 w-2/3 mb-2" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            data-testid="city-guides-grid"
          >
            {cities.map((c, idx) => (
              <Link
                key={c.slug}
                to={`/relo-news/cities/${c.slug}`}
                data-testid={`city-card-${c.slug}`}
                className="group block bg-white rounded-2xl border border-[#EFE6CF] overflow-hidden hover:border-[#2CA8A0]/50 hover:shadow-[0_8px_30px_rgba(10,31,46,0.08)] transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#F4ECD8]">
                  <img
                    src={c.hero_image_url}
                    alt={`${c.name} hero photo`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    width={1200}
                    height={800}
                    loading={idx < 6 ? 'eager' : 'lazy'}
                    fetchPriority={idx < 3 ? 'high' : 'auto'}
                    onError={(e) => {
                      // eslint-disable-next-line no-console
                      console.warn(`[CityGuides] hero failed to load: ${c.slug} → ${c.hero_image_url}`);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <div className="p-5">
                  <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] mb-2">
                    {c.state}
                  </p>
                  <h2 className="font-display text-2xl md:text-3xl text-[#0A1F2E] leading-tight mb-2">
                    {c.name}
                  </h2>
                  <p className="font-body text-sm text-[#0A1F2E]/75 leading-relaxed mb-3">
                    {c.tagline}
                  </p>
                  {c.stats && (
                    <p className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#E8A830] font-semibold">
                      ~${c.stats.avg_cost_living_usd}/mo · {c.stats.expat_population}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-1 mt-3 text-sm text-[#0A1F2E] font-medium group-hover:text-[#E8A830] transition">
                    Read the guide <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CityGuides;
